import { Flex, Text } from '@chakra-ui/react'
import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { reportingApi } from '~/apis/reportingApi'
import { useGetAllPhoneNumbersSingleRequest } from '~/queries/phoneNumberQueries'
import { useDateRangeWithSearchParams } from '~/types/Shared'

import {
  NumberComparisonDashboard,
  type NumberComparisonDashboardHandle,
  NumberComparisonFilters,
  ReportingI18nProvider,
} from '@mangovoice/reporting-ui'
import type { PhoneNumber } from '@mangovoice/types'

import { useDashboardContext } from './context/DashboardContext'

export const NumberComparisonPage = () => {
  const { t, i18n } = useTranslation()
  const { pbxId, timeZone } = useDashboardContext()
  const [dateRange, setDateRange] = useDateRangeWithSearchParams()
  const [selectedPhoneNumbers, setSelectedPhoneNumbers] = useState<
    PhoneNumber[]
  >([])
  const [canExport, setCanExport] = useState(false)
  const dashboardRef = useRef<NumberComparisonDashboardHandle>(null)

  const { results: phoneNumbers, isLoading: isPhoneNumbersLoading } =
    useGetAllPhoneNumbersSingleRequest()

  const phoneNumberMap = useMemo(() => {
    const map = new Map<string, PhoneNumber>()
    phoneNumbers.forEach((phone) => {
      map.set(phone.number, phone)
    })
    return map
  }, [phoneNumbers])

  return (
    <ReportingI18nProvider t={t} locale={i18n.language}>
      <Flex
        padding="1.25rem"
        flexDirection="column"
        alignItems="center"
        gap="0.25rem"
        flex="1 0 0"
        alignSelf="stretch"
        overflow="auto"
      >
        <NumberComparisonFilters
          dateTimeRange={dateRange}
          onValueChange={setDateRange}
          selectedPhoneNumbers={selectedPhoneNumbers}
          onPhoneNumberChange={setSelectedPhoneNumbers}
          phoneNumbers={phoneNumbers}
          isPhoneNumbersLoading={isPhoneNumbersLoading}
          onExportCsv={() => dashboardRef.current?.exportCsv()}
          isExportCsvDisabled={!canExport || !dateRange}
        />
        {dateRange ? (
          <NumberComparisonDashboard
            ref={dashboardRef}
            api={reportingApi}
            t={t}
            locale={i18n.language}
            sharedDateRange={dateRange}
            selectedPbxId={pbxId}
            timeZone={timeZone}
            selectedPhoneNumbers={selectedPhoneNumbers}
            phoneNumbers={phoneNumbers}
            phoneNumberMap={phoneNumberMap}
            isPhoneNumbersLoading={isPhoneNumbersLoading}
            onExportAvailabilityChange={setCanExport}
          />
        ) : (
          <Flex justifyContent="center" alignItems="center" height="100%">
            <Text>{t('dateTimeRange.selectDateRange')}</Text>
          </Flex>
        )}
      </Flex>
    </ReportingI18nProvider>
  )
}
